import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Procedure } from "../../../types/procedures";
import { ProcedureType } from "../../../types/procedure_type";
import { useQuote } from "../QuoteProvider";

// Above this the "focused" step stops being a shortlist and the treatment
// degrades into the full menu with extra chrome — fall back to default.
export const MAX_FOCUS_PROCEDURES = 5;

// Session stash so the focus survives in-app navigation: QuoteApp's step
// navigation (navigateToStep/prev) and the root auto-redirect use bare paths
// and drop the query string. Without the stash, a treatment visitor who taps
// Back lands on /select-procedure with no params and silently sees the
// control UI while still bucketed "focused".
const FOCUS_STASH_KEY = "campaign_focus_params";

type StashedFocusParams = { types: string[]; procedures: string[] };

// Accept both the bracketed convention (matches selected_procedures[]) and
// the bare name — ad-platform URL builders and click-trackers commonly strip
// or mangle the [] suffix, and a silently-dead campaign link is worse than a
// lenient parse.
function readParamList(searchParams: URLSearchParams, name: string): string[] {
  return [...searchParams.getAll(`${name}[]`), ...searchParams.getAll(name)];
}

// Shared links sometimes arrive double-encoded via paste-through tools (seen
// in prod 2026-09-09: `Breast%20Lift%20(with%20or%20without%20implant)` and
// `Breast%20Reduction` survived the browser's one decode pass still
// percent-encoded, so slug/name matching never hit and the visitor fell back
// to the default funnel). For any raw value containing `%`, also try a decoded
// variant alongside the original — decodeURIComponent throws URIError on
// malformed sequences, so that variant is just skipped.
function buildCandidateValues(rawValues: string[]): Set<string> {
  const candidates = new Set<string>();
  for (const raw of rawValues) {
    const trimmed = raw.trim().toLowerCase();
    if (trimmed) {
      candidates.add(trimmed);
    }
    if (raw.includes("%")) {
      try {
        const decoded = decodeURIComponent(raw).trim().toLowerCase();
        if (decoded) {
          candidates.add(decoded);
        }
      } catch {
        // malformed percent-encoding — skip the decoded variant
      }
    }
  }
  return candidates;
}

function loadStash(): StashedFocusParams | null {
  try {
    const raw = sessionStorage.getItem(FOCUS_STASH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.types) || !Array.isArray(parsed?.procedures)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveStash(stash: StashedFocusParams): void {
  try {
    sessionStorage.setItem(FOCUS_STASH_KEY, JSON.stringify(stash));
  } catch {
    /* private mode / blocked storage — focus just won't survive navigation */
  }
}

export type CampaignFocus = {
  /** focus_types/focus_procedures present in the URL or the session stash */
  hasFocusParams: boolean;
  /** focus resolved to a usable shortlist AND the session may enter the test */
  eligible: boolean;
  /** resolved focus procedures, position-sorted */
  focusProcedures: Procedure[];
  focusIds: Set<string>;
  focusType: "single" | "family";
  /** procedure types matched by focus_types (id or name, case-insensitive) */
  matchedTypes: ProcedureType[];
  focusTypesParam: string[];
  focusProceduresParam: string[];
};

// Stable result for the ~99% of traffic with no focus params: the memo below
// re-runs on every provider render (its catalog deps are rebuilt each render),
// so returning this frozen constant keeps both the work O(1) and the object
// identity stable for non-campaign sessions.
const EMPTY_FOCUS: CampaignFocus = Object.freeze({
  hasFocusParams: false,
  eligible: false,
  focusProcedures: [],
  focusIds: new Set<string>(),
  focusType: "family" as const,
  matchedTypes: [],
  focusTypesParam: [],
  focusProceduresParam: [],
});

/**
 * Resolves the focused-campaign-mode URL contract for the procedure step.
 *
 * Campaign links carry `focus_types[]` (procedure-type family, matched
 * case-insensitively against the type id and its name, so marketing can write
 * `?focus_types[]=Breast`) and/or `focus_procedures[]` — the value to use is the
 * **stable kebab-case `procedureSlug`** (e.g. `?focus_procedures[]=tummy-tuck`),
 * or the display name. Do NOT use the procedure's GraphQL `id`: in this Relay
 * funnel `procedure.id` is an opaque global id (base64 `Procedure:<oid>`), never
 * the raw Mongo `_id`, so a `_id` passed in the URL silently matches nothing
 * (this footgun cost several debug cycles). All matching is case-insensitive,
 * slug preferred over name. Both params also accept the bracket-less form. These
 * are intentionally separate from `selected_procedures[]`, which is coupled to
 * the shared-quote path in QuoteProvider. NOTE: a focus link must point at the
 * `/select-procedure` STEP — landing on the funnel root redirects (QuoteApp) and
 * drops the query before this hook runs.
 *
 * Resolution runs against `proceduresAllGender` (internal-filtered, active-
 * window-filtered, and special-of-shadow-filtered, but NOT gender-filtered —
 * gender defaults to FEMALE, which would otherwise veto male-procedure
 * campaigns). A base procedure currently shadowed by a live special won't
 * focus-resolve — that's intentional, same as a window-hidden procedure.
 * Bad/stale/unknown focus values simply yield `eligible: false` and the
 * default funnel renders. Never an empty list.
 */
export function useCampaignFocus(): CampaignFocus {
  const [searchParams] = useSearchParams();
  const quoteData = useQuote();

  const urlTypes = readParamList(searchParams, "focus_types");
  const urlProcedures = readParamList(searchParams, "focus_procedures");

  let focusTypesParam = urlTypes;
  let focusProceduresParam = urlProcedures;
  if (urlTypes.length > 0 || urlProcedures.length > 0) {
    saveStash({ types: urlTypes, procedures: urlProcedures });
  } else {
    const stash = loadStash();
    if (stash && (stash.types.length > 0 || stash.procedures.length > 0)) {
      focusTypesParam = stash.types;
      focusProceduresParam = stash.procedures;
    }
  }

  // NOTE on deps: the catalog deps are rebuilt by QuoteProvider on every
  // render, so for campaign sessions this memo recomputes per render — the
  // same order of work the control step's inline filters already do. Its real
  // job is the early EMPTY_FOCUS return for non-campaign traffic. Do NOT put
  // the returned object in a dep list expecting referential stability when
  // params are present.
  return useMemo(() => {
    const hasFocusParams =
      focusTypesParam.length > 0 || focusProceduresParam.length > 0;
    if (!hasFocusParams) {
      return EMPTY_FOCUS;
    }

    const wantedTypes = buildCandidateValues(focusTypesParam);
    const matchedTypes: ProcedureType[] = quoteData.procedureTypes.filter(
      (procedureType: ProcedureType) =>
        wantedTypes.has(procedureType.id.toLowerCase()) ||
        wantedTypes.has(procedureType.name.toLowerCase()),
    );
    const matchedTypeIds = new Set(matchedTypes.map((pt) => pt.id));

    const wantedProcedures = buildCandidateValues(focusProceduresParam);

    const resolutionList: Procedure[] =
      quoteData.proceduresAllGender ?? quoteData.procedures;
    const focusProcedures: Procedure[] = resolutionList
      .filter(
        (procedure) =>
          wantedProcedures.has(procedure.id.toLowerCase()) ||
          // Match the stable kebab-case slug first, then fall back to the
          // human-readable name (which marketing can rename, silently
          // breaking older campaign links).
          (procedure.procedureSlug
            ? wantedProcedures.has(procedure.procedureSlug.toLowerCase())
            : false) ||
          wantedProcedures.has(procedure.name.toLowerCase()) ||
          (procedure.procedureTypes || []).some((pt) =>
            matchedTypeIds.has(pt.id),
          ),
      )
      .sort(
        (a: any, b: any) => (a.position ?? Infinity) - (b.position ?? Infinity),
      );

    const focusIds = new Set(focusProcedures.map((procedure) => procedure.id));

    const eligible =
      focusProcedures.length > 0 &&
      focusProcedures.length <= MAX_FOCUS_PROCEDURES &&
      !quoteData.quoteShared &&
      !quoteData.quoteReloaded &&
      quoteData.selectedProcedureTypes.length === 0;

    return {
      hasFocusParams,
      eligible,
      focusProcedures,
      focusIds,
      focusType:
        focusProcedures.length === 1
          ? ("single" as const)
          : ("family" as const),
      matchedTypes,
      focusTypesParam,
      focusProceduresParam,
    };
  }, [
    focusTypesParam.join("\n"),
    focusProceduresParam.join("\n"),
    quoteData.proceduresAllGender,
    quoteData.procedureTypes,
    quoteData.quoteShared,
    quoteData.quoteReloaded,
    quoteData.selectedProcedureTypes.length,
  ]);
}
